import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  IconButton,
  Avatar
} from '@mui/material';
import {
  ArrowBack,
  Person,
  CheckCircle
} from '@mui/icons-material';

const MobilePractitionerSelection = ({
  practitioners = [],
  selectedPractitioner,
  onSelect,
  onBack,
  loading = false
}) => {
  // Loading state
  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="600">
            Choose a Practitioner
          </Typography>
        </Box>

        {[1, 2, 3].map(i => (
          <Paper key={i} sx={{ mb: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={60} height={60} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // No practitioners
  if (!practitioners || practitioners.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" fontWeight="600">
            Choose a Practitioner
          </Typography>
        </Box>

        <Alert severity="info">
          No practitioners available for this service. Please contact the clinic directly.
        </Alert>
      </Box>
    );
  }

  // Render practitioner card
  const PractitionerCard = ({ practitioner, isAny = false }) => {
    const isSelected = isAny
      ? selectedPractitioner?.id === 'any'
      : selectedPractitioner?.id === practitioner.id;

    return (
      <Paper
        onClick={() => onSelect(practitioner)}
        sx={{
          p: 2,
          mb: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          transition: 'all 0.2s ease',
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? 'primary.light' : 'white',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateX(4px)',
            boxShadow: 2
          },
          '&:active': {
            transform: 'scale(0.98)'
          }
        }}
      >
        {/* Avatar */}
        <Avatar
          src={practitioner.photo_url}
          sx={{
            width: 64,
            height: 64,
            bgcolor: isAny ? 'primary.main' : 'grey.300'
          }}
        >
          <Person sx={{ fontSize: 32 }} />
        </Avatar>

        {/* Info */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="600"
            sx={{ color: isSelected ? 'primary.dark' : 'text.primary' }}
          >
            {practitioner.name}
          </Typography>

          {practitioner.title && (
            <Typography variant="body2" color="text.secondary">
              {practitioner.title}
            </Typography>
          )}

          {isAny && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              See all available time slots
            </Typography>
          )}
        </Box>

        {/* Selected indicator */}
        {isSelected && (
          <CheckCircle sx={{ color: 'primary.main', fontSize: 28 }} />
        )}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="600">
          Choose a Practitioner
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select who you'd like to see for your appointment
      </Typography>

      {/* "Any Practitioner" option */}
      <PractitionerCard
        isAny={true}
        practitioner={{
          id: 'any',
          name: 'Any Available Practitioner',
          description: 'Choose this to see all available times'
        }}
      />

      {/* Divider */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        my: 2
      }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.secondary">
          OR CHOOSE SPECIFIC
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* Individual practitioners */}
      {practitioners.map(practitioner => (
        <PractitionerCard
          key={practitioner.id}
          practitioner={practitioner}
        />
      ))}
    </Box>
  );
};

export default MobilePractitionerSelection;
